import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun } from 'lucide-react';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    // Check if user has a manual preference stored
    const storedPreference = localStorage.getItem('darkModePreference');
    if (storedPreference) {
      // User has manually set a preference
      const prefersDark = storedPreference === 'dark';
      setIsDarkMode(prefersDark);
      if (prefersDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } else {
      // No manual preference - use time-based automatic switching
      const checkTimeAndSetTheme = () => {
        const hour = new Date().getHours();
        // Dark mode between 6 PM (18:00) and 6 AM (6:00)
        const shouldBeDark = hour >= 18 || hour < 6;
        setIsDarkMode(shouldBeDark);
        if (shouldBeDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      };
      checkTimeAndSetTheme();
      // Check every minute for time changes
      const interval = setInterval(checkTimeAndSetTheme, 60000);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearInterval(interval);
      };
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    document.documentElement.classList.toggle('dark');
    // Store user's manual preference
    localStorage.setItem('darkModePreference', newMode ? 'dark' : 'light');
  };
  // Add function to reset to automatic mode (optional - can be called from settings)
  const resetToAutomatic = () => {
    localStorage.removeItem('darkModePreference');
    const hour = new Date().getHours();
    const shouldBeDark = hour >= 18 || hour < 6;
    setIsDarkMode(shouldBeDark);
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  // Calculate opacity and blur based on scroll position
  const calculateOpacity = () => {
    // Start with opacity 0 when not scrolled, max at 0.9
    const baseOpacity = Math.min(scrollY / 100, 0.9);
    return scrollY > 10 ? baseOpacity : 0;
  };
  const calculateBlur = () => {
    // Start with no blur, max at 12px
    return Math.min(scrollY / 10, 12);
  };
  const navbarStyle = {
    backgroundColor: scrollY > 10 ? `rgba(${isDarkMode ? '10, 14, 23' : '255, 255, 255'}, ${calculateOpacity()})` : 'transparent',
    backdropFilter: scrollY > 10 ? `blur(${calculateBlur()}px)` : 'none'
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrollY > 10 ? 'py-4 shadow-sm' : 'py-6'}`} style={navbarStyle} data-id="element-105">
      <div className="container mx-auto px-6 flex justify-between items-center" data-id="element-106">
        <div className="flex items-center" data-id="element-107">
          <Link to="/" className="flex items-center" data-id="element-108">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/t9uuVYeiHkVY4wfY5mcJTd/logo.svg" alt="JoinOmu" className="h-8" data-id="element-109" />
          </Link>
        </div>
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8" data-id="element-110">
          <Link to="/" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] transition-colors ${location.pathname === '/' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} data-id="element-111">
            Home
          </Link>
          <Link to="/weight-loss" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] transition-colors ${location.pathname === '/weight-loss' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} data-id="element-112">
            Weight Loss
          </Link>
          <Link to="/mens-health" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] transition-colors ${location.pathname === '/mens-health' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} data-id="element-113">
            Hormone Therapy
          </Link>
          <Link to="/investors" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] transition-colors ${location.pathname === '/investors' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} data-id="element-114">
            For Investors
          </Link>
          <a href="https://www.indeed.com/viewjob?jk=da22fcc0bd6a38ab&from=shareddesktop_copy" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] transition-colors" data-id="element-115">
            For Providers
          </a>
          <a href="https://app.joinomu.com/patient/login" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#C85A15] text-white font-medium py-2 px-6 rounded-full hover:shadow-lg transition-all" data-id="element-116">
            Log In
          </a>
          <button onClick={toggleDarkMode} className="ml-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Toggle dark mode (currently automatic based on time)" data-id="element-117">
            {isDarkMode ? <Sun size={20} data-id="element-118" /> : <Moon size={20} data-id="element-119" />}
          </button>
        </nav>
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center" data-id="element-120">
          <button onClick={toggleDarkMode} className="mr-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Toggle dark mode (currently automatic based on time)" data-id="element-121">
            {isDarkMode ? <Sun size={20} data-id="element-122" /> : <Moon size={20} data-id="element-123" />}
          </button>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-124">
            {isMenuOpen ? <X size={24} data-id="element-125" /> : <Menu size={24} data-id="element-126" />}
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 shadow-lg" style={{
      backgroundColor: `rgba(${isDarkMode ? '10, 14, 23' : '255, 255, 255'}, 0.9)`,
      backdropFilter: 'blur(12px)'
    }} data-id="element-127">
          <div className="container mx-auto px-6 py-4 flex flex-col space-y-4" data-id="element-128">
            <Link to="/" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] py-2 transition-colors ${location.pathname === '/' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} onClick={() => setIsMenuOpen(false)} data-id="element-129">
              Home
            </Link>
            <Link to="/weight-loss" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] py-2 transition-colors ${location.pathname === '/weight-loss' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} onClick={() => setIsMenuOpen(false)} data-id="element-130">
              Weight Loss
            </Link>
            <Link to="/mens-health" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] py-2 transition-colors ${location.pathname === '/mens-health' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} onClick={() => setIsMenuOpen(false)} data-id="element-131">
              Hormone Therapy
            </Link>
            <Link to="/investors" className={`text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] py-2 transition-colors ${location.pathname === '/investors' ? 'text-[#C85A15] dark:text-[#BBDDFF]' : ''}`} onClick={() => setIsMenuOpen(false)} data-id="element-132">
              For Investors
            </Link>
            <a href="https://www.indeed.com/viewjob?jk=da22fcc0bd6a38ab&from=shareddesktop_copy" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-gray-300 hover:text-[#C85A15] dark:hover:text-[#BBDDFF] py-2 transition-colors" onClick={() => setIsMenuOpen(false)} data-id="element-133">
              For Providers
            </a>
            <a href="https://app.joinomu.com/patient/login" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#C85A15] text-white font-medium py-2 px-6 rounded-full hover:shadow-lg transition-all w-full text-center" data-id="element-134">
              Log In
            </a>
          </div>
        </div>}
    </header>;
}